import React, {
  useRef, useEffect, useState, useCallback,
  forwardRef, useImperativeHandle, useMemo
} from 'react';
import * as d3 from 'd3';
import styles from './GanttChart.module.css';

const HEADER_H = 44;
const TICK_SIZE = 12;
const DATE_LABEL_Y = 18;
const PILL_Y = 32;
const LONG_TICK_Y2 = -(HEADER_H - 8);
const ROW_HEIGHT = 48;

const ELLIPSIS = '…';
const clamp = (v, min, max) => Math.max(min, Math.min(v, max));
const ms = {
  SECOND: 1_000,
  MINUTE: 60_000,
  HOUR: 3_600_000,
  DAY: 86_400_000,
};

function diffInfo(deviceMs, globalMs) {
  if (!isFinite(globalMs) || globalMs <= 0) return { label: '', color: '#888' };
  const delta = deviceMs - globalMs;
  const pct = Math.round(Math.abs(delta) / globalMs * 100);
  if (delta > 0) return { label: `+${pct}% slower`, color: '#EF5350' };
  if (delta < 0) return { label: `-${pct}% faster`, color: '#09c372' };
  return { label: 'same', color: '#888' };
}

function textLength(sel) {
  const node = sel.node?.();
  if (!node || !node.getComputedTextLength) return 0;
  return node.getComputedTextLength();
}

function fitWithEllipsis(sel, fullText, maxWidth) {
  if (maxWidth <= 0) { sel.text(''); return; }
  sel.text(fullText);
  if (textLength(sel) <= maxWidth) return;
  let lo = 0, hi = fullText.length;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    const trial = fullText.slice(0, mid) + ELLIPSIS;
    sel.text(trial);
    if (textLength(sel) <= maxWidth) lo = mid + 1; else hi = mid;
  }
  const finalTxt = fullText.slice(0, Math.max(0, lo - 1)) + ELLIPSIS;
  sel.text(finalTxt);
}

function pillPath(w, h, rLeft = 4, tipLen = 12, tipRound = 5) {
  const halfW = w / 2; const halfH = h / 2; const x0 = -halfW + rLeft; const bodyRight = halfW - tipLen;
  const cpX = bodyRight + Math.max(0, tipRound);
  return [
    `M${x0},${-halfH}`,
    `H${bodyRight}`,
    `Q${cpX},${-halfH} ${halfW},0`,
    `Q${cpX},${halfH} ${bodyRight},${halfH}`,
    `H${x0}`,
    `a${rLeft},${rLeft} 0 0 1 -${rLeft},-${rLeft}`,
    `V${-halfH + rLeft}`,
    `a${rLeft},${rLeft} 0 0 1 ${rLeft},-${rLeft}`,
    'Z'
  ].join(' ');
}

function getFixedTooltipPos(evt, w = 260, h = 110, offset = 12) {
  const cx = evt?.clientX ?? 0; const cy = evt?.clientY ?? 0;
  let x = cx + offset; let y = cy + offset;
  if (typeof window !== 'undefined') {
    if (x + w > window.innerWidth) x = cx - w - offset;
    if (y + h > window.innerHeight) y = cy - h - offset;
  }
  return { x, y };
}

function toDataKey(list) {
  return JSON.stringify(
    (list || []).map(d => ({
      name: d.name,
      status: d.status,
      tasks: (d.tasks || []).map(t => ({
        task: t.task,
        status: t.status,
        color: t.color,
        start: +new Date(t.start),
        end: +new Date(t.end),
      }))
    }))
  );
}

function makeTimeFormatter(spanMs) {
  const fmt =
    spanMs <= ms.MINUTE ? d3.timeFormat('%H:%M:%S.%L') :
    spanMs <= ms.HOUR   ? d3.timeFormat('%H:%M:%S')     :
                          d3.timeFormat('%H:%M');
  return (d) => fmt(d);
}

function formatCursorTime(spanMs) {
  return spanMs <= ms.HOUR ? d3.timeFormat('%Y-%m-%d %H:%M:%S.%L') : d3.timeFormat('%Y-%m-%d %H:%M');
}

function formatDurationMs(totalMs) {
  const msPart = Math.max(0, Math.floor(totalMs % 1000));
  const totalSec = Math.floor(totalMs / 1000);
  if (totalSec === 0) return `${totalMs}ms`;
  const s = totalSec % 60;
  const totalMin = Math.floor(totalSec / 60);
  if (totalMin === 0) return `${s}.${String(msPart).padStart(3,'0')}s`;
  const m = totalMin % 60;
  const h = Math.floor(totalMin / 60);
  if (h === 0) return `${m}m ${s}.${String(msPart).padStart(3,'0')}s`;
  return `${h}h ${m}m ${s}.${String(msPart).padStart(3,'0')}s`;
}

const statusColors = {
  active: '#09ff00',
  success: '#007bff',
  complete: '#007bff',
  offline: '#9E9E9E'
};

const taskStatuses = ['waiting','running','completed','error','timeout'];
const taskStatusColors = {
  waiting: '#9E9E9E',
  running: '#09ff00',
  completed: '#007bff',
  error: '#EF5350',
  timeout: '#FF7043'
};
const taskNames = ['Init','Job Request','Job Ready','Model Download','Training','Evaluation','Test','Complete','Aggregation Out','Timeout'];

const nowBase = new Date();
const baseDates = [
  new Date(nowBase.getFullYear(), nowBase.getMonth(), nowBase.getDate() - 1),
  new Date(nowBase.getFullYear(), nowBase.getMonth(), nowBase.getDate()),
  new Date(nowBase.getFullYear(), nowBase.getMonth(), nowBase.getDate() + 1),
];
const rand = (n) => Math.floor(Math.random() * n);
const randomDate = (minHour = 0, maxHour = 22) => {
  const base = baseDates[rand(baseDates.length)];
  const d = new Date(base);
  d.setHours(minHour + rand(Math.max(1, maxHour - minHour)));
  d.setMinutes(rand(60));
  d.setSeconds(rand(60));
  d.setMilliseconds(rand(1000));
  return d;
};
const dummyDevices = Array.from({ length: 50 }, (_, i) => {
  const taskCount = 1 + rand(3);
  const tasks = Array.from({ length: taskCount }, () => {
    const start = randomDate();
    const end = new Date(start.getTime() + 1_000 * (30 + Math.random() * 300) + rand(1000));
    const status = taskStatuses[rand(taskStatuses.length)];
    return { task: taskNames[rand(taskNames.length)], status, color: taskStatusColors[status], start, end };
  });
  const statuses = ['active', 'success', 'offline'];
  return { name: `Device ${i + 1}`, status: statuses[rand(statuses.length)], tasks };
});

function useMeasureWidth(refEl) {
  const [w, setW] = useState(0);
  useEffect(() => {
    if (!refEl.current) return;
    let raf = null;
    const ro = new ResizeObserver(([entry]) => {
      const width = Math.round(entry.contentRect.width);
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setW(prev => (prev !== width ? width : prev)));
    });
    ro.observe(refEl.current);
    return () => { if (raf) cancelAnimationFrame(raf); ro.disconnect(); };
  }, [refEl]);
  return w;
}

function useNowTick(intervalMs = 1000) {
  const [tick, setTick] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setTick(Date.now()), intervalMs); return () => clearInterval(id); }, [intervalMs]);
  return tick;
}

function normalizeJobName(raw) {
  if (!raw) return '';
  let s = String(raw);
  s = s.replace(/\(\s*(?:round|r)\s*\d+\s*\)/ig, ' ');
  s = s.replace(/^\s*(?:(?:\d+\s*round)|(?:round\s*\d+)|(?:r\s*\d+)|(?:\d+\s*r))\s*[-_:]?\s*/i, ' ');
  s = s.replace(/\s*(?:(?:\d+\s*round)|(?:round\s*\d+)|(?:r\s*\d+)|(?:\d+\s*r))\s*$/i, ' ');
  s = s.replace(/\s{2,}/g, ' ').trim();
  return s;
}

function computeJobAverages(tasks = []) {
  const byJob = new Map();
  for (const t of tasks) {
    const job = normalizeJobName(t?.task);
    if (!job) continue;
    const dur = +new Date(t.end) - +new Date(t.start);
    if (!isFinite(dur) || dur < 0) continue;
    if (!byJob.has(job)) byJob.set(job, []);
    byJob.get(job).push(dur);
  }
  const pref = taskNames.map(normalizeJobName);
  const keys = Array.from(byJob.keys());
  const ordered = [
    ...pref.filter(k => byJob.has(k)),
    ...keys.filter(k => !pref.includes(k)).sort((a,b)=>a.localeCompare(b))
  ].filter((v,i,a)=>a.indexOf(v)===i);

  return ordered.map(job => {
    const arr = byJob.get(job) || [];
    const avgMs = arr.reduce((s,v)=>s+v,0) / (arr.length || 1);
    return { job, avgMs, count: arr.length };
  });
}

function computeGlobalJobAverages(devices = []) {
  const allTasks = devices.flatMap(d => d?.tasks || []);
  return computeJobAverages(allTasks);
}

function indexByJob(averages = []) {
  const m = new Map();
  for (const a of averages) m.set(a.job, a);
  return m;
}

export default forwardRef(function GanttChart(
  {
    className ='',
    devices = [],
    startTime,
    endTime,
    onRowClick,
    labelSingleLine = false,
    isModal = false,
    keepZoomOnModalOpen = true
  },
  ref
) {
  const wrapperRef = useRef(null);
  const axisRef = useRef(null);
  const svgRef = useRef(null);

  const isLoading = !devices || devices.length === 0;
  const safeDevices = useMemo(() => (devices?.length ? devices : dummyDevices), [devices]);
  const inModal = !!isModal;

  const { domainStart, domainEnd } = useMemo(() => {
    const allTasks = safeDevices.flatMap(d => d.tasks || []);
    const start = allTasks.length ? new Date(Math.min(...allTasks.map(t => +new Date(t.start)))) : new Date();
    const end   = allTasks.length ? new Date(Math.max(...allTasks.map(t => +new Date(t.end))))   : new Date(Date.now() + ms.HOUR);
    return { domainStart: startTime || start, domainEnd: endTime || end };
  }, [safeDevices, startTime, endTime]);

  const baseDomainRef = useRef([null, null]);
  const [zoomDomain, setZoomDomain] = useState([domainStart, domainEnd]);

  useEffect(() => {
    baseDomainRef.current = [domainStart, domainEnd];
  }, [domainStart, domainEnd]);

  const prevZoomDomainRef = useRef(zoomDomain);
  const prevDataKeyRef = useRef(null);
  const shouldAnimNextZoomRef = useRef(false);

  const margin = useMemo(() => ({ top: HEADER_H, right: 20, bottom: 20, left: 200 }), []);
  const containerWidth = useMeasureWidth(wrapperRef);
  const innerWidth = Math.max(1, containerWidth - margin.left - margin.right);
  const dataKey = useMemo(() => toDataKey(safeDevices), [safeDevices]);

  const [mouseX, setMouseX] = useState(null);
  const nowTick = useNowTick(1000);

  const [isBrushing, setIsBrushing] = useState(false);
  const [brushStartX, setBrushStartX] = useState(null);
  const [brushEndX, setBrushEndX] = useState(null);
  const lastClickRef = useRef({ t: 0, x: 0 });

  const inInitialLayoutRef = useRef(true);
  useEffect(() => {
    inInitialLayoutRef.current = true;
    const id = setTimeout(() => { inInitialLayoutRef.current = false; }, 160);
    return () => clearTimeout(id);
  }, [containerWidth]);

  const handleDoubleClick = useCallback(() => {
    shouldAnimNextZoomRef.current = true;
    setZoomDomain(baseDomainRef.current);
  }, []);

  useImperativeHandle(ref, () => ({
    setSpanTo(unit) {
      const unitMap = { sec: ms.SECOND, second: ms.SECOND, min: ms.MINUTE, hour: ms.HOUR, day: ms.DAY };
      const span = unitMap[unit] ?? ms.HOUR;
      if (!innerWidth) return;
      const xScale = d3.scaleTime().domain(zoomDomain).range([0, innerWidth]);
      const [min, max] = zoomDomain;
      const px = mouseX != null ? clamp(mouseX, 0, innerWidth) : innerWidth / 2;
      const anchorTime = mouseX != null ? xScale.invert(px) : new Date((+min + +max) / 2);
      let newMin = new Date(+anchorTime - (px / innerWidth) * span);
      let newMax = new Date(+newMin + span);
      const [bd0, bd1] = baseDomainRef.current;
      if (newMin < bd0) { newMin = new Date(bd0); newMax = new Date(+newMin + span); }
      if (newMax > bd1) { newMax = new Date(bd1); newMin = new Date(+newMax - span); }
      shouldAnimNextZoomRef.current = true;
      setZoomDomain([newMin, newMax]);
    },
    zoomBy(unit, direction = 'in') {
      const step = { sec: ms.SECOND, second: ms.SECOND, min: ms.MINUTE, hour: ms.HOUR, day: ms.DAY }[unit] ?? ms.HOUR;
      const [min, max] = zoomDomain; const currSpan = +max - +min;
      const newSpan = Math.max(ms.SECOND, currSpan + (direction === 'in' ? -step : step));
      const xScale = d3.scaleTime().domain(zoomDomain).range([0, innerWidth]);
      const px = mouseX != null ? clamp(mouseX, 0, innerWidth) : innerWidth / 2;
      const anchorTime = mouseX != null ? xScale.invert(px) : new Date((+min + +max) / 2);
      let newMin = new Date(+anchorTime - (px / innerWidth) * newSpan);
      let newMax = new Date(+newMin + newSpan);
      const [bd0, bd1] = baseDomainRef.current;
      if (newMin < bd0) { newMin = new Date(bd0); newMax = new Date(+newMin + newSpan); }
      if (newMax > bd1) { newMax = new Date(bd1); newMin = new Date(+newMax - newSpan); }
      shouldAnimNextZoomRef.current = true;
      setZoomDomain([newMin, newMax]);
    },
  }), [zoomDomain, innerWidth, mouseX]);

  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0, y: 0,
    kind: 'task',
    task: '', duration: '', name: '', start: '', end: '',
    deviceDisplayName: '',
    averages: [],
    hideGlobalForServer: false
  });

  const dataChangedRef = useRef(false);
  useEffect(() => {
    dataChangedRef.current = prevDataKeyRef.current !== dataKey;
  }, [dataKey]);

  useEffect(() => {
    if (!keepZoomOnModalOpen) {
      shouldAnimNextZoomRef.current = true;
      setZoomDomain(baseDomainRef.current);
      return;
    }
    setZoomDomain(([curMin, curMax]) => {
      const [bd0, bd1] = baseDomainRef.current;
      if (!curMin || !curMax) return [bd0, bd1];
      let nm = new Date(Math.max(+bd0, +curMin));
      let nx = new Date(Math.min(+bd1, +curMax));
      if (+nx - +nm < ms.SECOND) return [bd0, bd1];
      return [nm, nx];
    });
  }, [domainStart, domainEnd, keepZoomOnModalOpen]);

  useEffect(() => {
    if (!containerWidth || !safeDevices.length) return;

    const width = containerWidth;
    const height = safeDevices.length * ROW_HEIGHT;
    const xScale = d3.scaleTime().domain(zoomDomain).range([0, innerWidth]);
    const oldXScale = d3.scaleTime().domain(prevZoomDomainRef.current).range([0, innerWidth]);
    const isZoomAnim = shouldAnimNextZoomRef.current;
    const spanMs = +zoomDomain[1] - +zoomDomain[0];
    const tickFmt = makeTimeFormatter(spanMs);

    if (axisRef.current) {
      const axisSvg = d3.select(axisRef.current).attr('width', width).attr('height', HEADER_H);
      let base = axisSvg.select('.base-layer');
      if (base.empty()) base = axisSvg.append('g').attr('class', 'base-layer');
      base.selectAll('*').remove();

      base.append('line')
        .attr('x1', margin.left).attr('x2', margin.left)
        .attr('y1', 0).attr('y2', HEADER_H)
        .attr('class', styles.headerColumnDivider);

      const xAxis = d3.axisTop(xScale).ticks(12).tickSize(TICK_SIZE + 3).tickFormat(tickFmt);

      const xAxisG = base.append('g')
        .attr('transform', `translate(${margin.left}, ${HEADER_H - 1})`)
        .attr('class', styles.axis)
        .call(xAxis);
      xAxisG.select('.domain').remove();
      xAxisG.selectAll('text').attr('x', 4).attr('y', -(TICK_SIZE / 2)).attr('text-anchor', 'start').attr('class', styles.dateLabelTick);

      xAxisG.selectAll('g.tick').each(function(d) {
        if (!d) return;
        const date = new Date(d);
        if (date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0) {
          d3.select(this).select('line').attr('y2', LONG_TICK_Y2);
        }
      });

      const dayStarts = d3.timeDay.range(d3.timeDay.floor(zoomDomain[0]), zoomDomain[1]);
      const dateLabels = [];
      dayStarts.forEach(d => {
        const label = base.append('text')
          .attr('x', margin.left + xScale(d) + 6)
          .attr('y', DATE_LABEL_Y)
          .attr('class', styles.dateLabelBig)
          .attr('text-anchor', 'start')
          .text(d3.timeFormat('%Y-%m-%d %a')(d));
        dateLabels.push(label);
      });
      if (dateLabels.length >= 2) {
        const x1 = parseFloat(dateLabels[0].attr('x')); const x2 = parseFloat(dateLabels[1].attr('x'));
        const gap = x2 - x1; const minSpacing = 100, maxSpacing = 150;
        const opacity = clamp((gap - minSpacing) / (maxSpacing - minSpacing), 0, 1);
        dateLabels[0].style('opacity', opacity);
      }
    }

    if (svgRef.current) {
      const svg = d3.select(svgRef.current).attr('width', width).attr('height', height + margin.bottom);
      let contentRoot = svg.select('.content-root');
      if (contentRoot.empty()) contentRoot = svg.append('g').attr('class', 'content-root');
      contentRoot.selectAll('*').remove();

      const content = contentRoot.append('g').attr('transform', `translate(${margin.left}, 0)`);

      const nowDate = new Date();
      if (+nowDate >= +zoomDomain[0] && +nowDate <= +zoomDomain[1]) {
        const nowX = xScale(nowDate);
        content.append('line')
          .attr('x1', nowX).attr('x2', nowX)
          .attr('y1', 0).attr('y2', height)
          .attr('class', styles.nowMarker)
          .style('pointer-events', 'none');
      }

      const rows = content.selectAll('g.task-row').data(safeDevices).enter().append('g')
        .attr('class', `${styles.taskRow} task-row`)
        .attr('transform', (_, i) => `translate(0, ${i * ROW_HEIGHT})`)
        .style('cursor', onRowClick ? 'pointer' : null)
        .on('click', function(_, d){
          if (onRowClick) onRowClick(d);
        });

      rows.append('rect')
        .attr('x', -margin.left)
        .attr('y', 0)
        .attr('width', width + margin.left)
        .attr('height', ROW_HEIGHT)
        .attr('fill', 'transparent')
        .style('cursor', onRowClick ? 'pointer' : null)
        .on('click', function(_, d){ if (onRowClick) onRowClick(d); });

      rows.append('line')
        .attr('x1', -margin.left).attr('x2', width)
        .attr('y1', 0).attr('y2', 0)
        .attr('class', styles.rowTopDivider);

      rows.insert('rect', ':first-child')
        .attr('class', styles.rowHoverRect)
        .attr('x', 0).attr('y', 0)
        .attr('width', innerWidth + margin.left)
        .attr('height', ROW_HEIGHT)
        .style('pointer-events', 'none');

      const ROW_STAGGER = 40, TASK_STAGGER = 60, BAR_DUR = 600, DOT_DUR = 300, TXT_DUR = 250;
      const doAppear = inInitialLayoutRef.current || (prevDataKeyRef.current !== dataKey);

      rows.each(function (deviceData, rowIdx) {
        const row = d3.select(this);
        const groups = row.selectAll('g.task-group').data(deviceData.tasks || []).enter().append('g').attr('class', 'task-group');

        groups.each(function (t, taskIdx) {
          const g = d3.select(this);
          const startNew = xScale(new Date(t.start));
          const endNew   = xScale(new Date(t.end));
          const startOld = oldXScale(new Date(t.start));
          const endOld   = oldXScale(new Date(t.end));

          const xMin = xScale.range()[0] - 100;
          const xMax = xScale.range()[1] + 80;
          if ((endNew <= xMin && endOld <= xMin) || (startNew >= xMax && startOld >= xMax)) return;

          const clampedStartX  = Math.max(xMin, startNew);
          const clampedEndX    = Math.min(xMax, endNew);
          const widthRect      = Math.max(0, clampedEndX - clampedStartX);

          const clampedStartX0 = Math.max(xMin, startOld);
          const clampedEndX0   = Math.min(xMax, endOld);
          const widthRect0     = Math.max(0, clampedEndX0 - clampedStartX0);

          const h = ROW_HEIGHT * .65;
          const cy = ROW_HEIGHT / 2;
          const isJobReady = ((t.task || '') + '').toLowerCase().includes('job ready');
          const durationMs = Math.max(0, (+new Date(t.end) - +new Date(t.start)));
          const durationText = isJobReady ? '' : formatDurationMs(durationMs);

          const minLabelWidth = 28; const smallBox = 24; const smallRadius = 6;
          const drawX  = clamp(clampedStartX, xMin, xMax - smallBox);
          const drawX0 = clamp(clampedStartX0, xMin, xMax - smallBox);

          const delay = rowIdx * ROW_STAGGER + taskIdx * TASK_STAGGER;

          if (widthRect < minLabelWidth) {
            const cx  = drawX  + smallBox / 2;
            const cx0 = drawX0 + smallBox / 2;

            const chipG = g.append('g')
              .attr('class', 'task-small-chip')
              .attr('transform', isZoomAnim ? `translate(${cx0},${cy}) scale(1)` : `translate(${cx},${cy}) scale(${doAppear ? 0 : 1})`);

            chipG.append('rect')
              .attr('x', -smallBox / 2).attr('y', -smallBox / 2)
              .attr('width', smallBox).attr('height', smallBox)
              .attr('rx', smallBox).attr('ry', smallBox)
              .attr('class', styles.taskSmall);

            chipG.append('circle')
              .attr('cx', 0).attr('cy', 0).attr('r', smallRadius)
              .attr('fill', t.color).attr('stroke', t.color)
              .attr('stroke-opacity', 0.4).attr('stroke-width', 2)
              .attr('class', styles.statusCircle);

            if (isZoomAnim) {
              d3.timeout(() => d3.select(chipG.node()).transition().duration(450).ease(d3.easeCubicInOut)
                .attr('transform', `translate(${cx},${cy}) scale(1)`));
            } else if (doAppear) {
              d3.timeout(() => d3.select(chipG.node()).transition().delay(delay).duration(DOT_DUR).ease(d3.easeCubicOut)
                .attr('transform', `translate(${cx},${cy}) scale(1)`));
            }

            g.append('rect')
              .attr('x', drawX - 4).attr('y', cy - smallBox / 2 - 4)
              .attr('width', smallBox + 8).attr('height', smallBox + 8)
              .style('fill', 'transparent');
          } else {
            const bar = g.append('rect')
              .attr('y', cy - h / 2).attr('height', h)
              .attr('class', styles.task)
              .attr('x', isZoomAnim ? clampedStartX0 : clampedStartX)
              .attr('width', isZoomAnim ? widthRect0 : (doAppear ? 0 : widthRect));

            const dot = g.append('circle')
              .attr('cy', cy)
              .attr('fill', t.color).attr('stroke', t.color)
              .attr('stroke-opacity', 0.4).attr('stroke-width', 2)
              .attr('class', styles.statusCircle)
              .attr('cx', isZoomAnim ? (clampedStartX0 + 14) : (clampedStartX + 14))
              .attr('r', doAppear && !isZoomAnim ? 0 : 6);

            const leftPad = 28; const rightPad = 12; const gap = 8;

            const labelStatusSel = t.task+" ("+t.status+")";
            const labelSel = g.append('text')
              .attr('y', cy).attr('dy', '0.35em')
              .attr('class', styles.taskLabel)
              .text(labelStatusSel)
              .attr('opacity', doAppear && !isZoomAnim ? 0 : 1)
              .attr('x', (isZoomAnim ? clampedStartX0 : clampedStartX) + leftPad);

            const labelStartX = clampedStartX + leftPad;

            let durationSel = null;
            let durationW = 0;
            if (!isJobReady) {
              durationSel = g.append('text')
                .attr('y', cy).attr('dy', '0.35em').attr('text-anchor', 'end')
                .attr('class', styles.taskDuration)
                .text(durationText)
                .attr('opacity', doAppear && !isZoomAnim ? 0 : 1)
                .attr('x', (isZoomAnim ? clampedStartX0 + widthRect0 : clampedStartX + widthRect) - rightPad);
              durationW = textLength(durationSel);
            }

            const labelMaxRightOverall = clampedStartX + widthRect - rightPad;
            if (!isJobReady && durationSel) {
              const durationLeftX = clampedStartX + widthRect - rightPad - durationW;
              const labelMaxRightIfDuration = durationLeftX - gap;
              const naturalLabelW = textLength(labelSel);
              if (naturalLabelW + labelStartX > labelMaxRightIfDuration) {
                if (durationSel) durationSel.remove();
                const maxLabelWidth = labelMaxRightOverall - labelStartX;
                fitWithEllipsis(labelSel, labelStatusSel, maxLabelWidth);
              } else {
                const maxLabelWidth = labelMaxRightIfDuration - labelStartX;
                fitWithEllipsis(labelSel, labelStatusSel, maxLabelWidth);
              }
            } else {
              const maxLabelWidth = labelMaxRightOverall - labelStartX;
              fitWithEllipsis(labelSel, labelStatusSel, maxLabelWidth);
            }

            if (isZoomAnim) {
              d3.timeout(() => {
                bar.transition().duration(450).ease(d3.easeCubicInOut).attr('x', clampedStartX).attr('width', widthRect);
                dot.transition().duration(450).ease(d3.easeCubicInOut).attr('cx', clampedStartX + 14).attr('r', 6);
                if (!isJobReady && durationSel) durationSel.transition().duration(450).ease(d3.easeCubicInOut).attr('x', clampedStartX + widthRect - rightPad).attr('opacity', 1);
                labelSel.transition().duration(450).ease(d3.easeCubicInOut).attr('x', clampedStartX + leftPad).attr('opacity', 1);
              });
            } else if (doAppear) {
              d3.timeout(() => {
                bar.transition().delay(delay).duration(BAR_DUR).ease(d3.easeCubicOut).attr('width', widthRect).attr('x', clampedStartX);
                dot.transition().delay(delay + 120).duration(DOT_DUR).ease(d3.easeCubicOut).attr('r', 6).attr('cx', clampedStartX + 14);
                labelSel.transition().delay(delay + 400).duration(TXT_DUR).attr('opacity', 1);
                if (!isJobReady && durationSel && !durationSel.empty()) durationSel.transition().delay(delay + 400).duration(TXT_DUR).attr('opacity', 1);
              });
            }
          }

          const fmtFull = d3.timeFormat('%Y-%m-%d %H:%M:%S.%L');
          if (!inModal) {
            g.on('mouseenter', (event) => {
              const { x, y } = getFixedTooltipPos(event, 260, 110, 12);
              setTooltip({
                visible: true,
                x, y,
                kind: 'task',
                task: t.task + " ("+t.status+")",
                duration: isJobReady ? '' : formatDurationMs(durationMs),
                name: deviceData.name==='server'?'FL Server':deviceData.name,
                start: fmtFull(new Date(t.start)),
                end: isJobReady ? '' : fmtFull(new Date(t.end)),
                deviceDisplayName: '',
                averages: [],
                hideGlobalForServer: false
              });
            }).on('mousemove', (event) => {
              setTooltip(prev => prev.visible ? { ...prev, ...getFixedTooltipPos(event, 260, 110, 12) } : prev);
            }).on('mouseleave', () => setTooltip(prev => ({ ...prev, visible: false })));
          }
        });
      });

      d3.select(svgRef.current).selectAll('line.column-divider').data([1]).join('line')
        .attr('class', `column-divider ${styles.columnDivider}`)
        .attr('x1', margin.left).attr('x2', margin.left)
        .attr('y1', 0).attr('y2', height);

      const labelRows = content.selectAll('g.label-row').data(safeDevices).enter().append('g')
        .attr('class', 'label-row')
        .attr('transform', (_, i) => `translate(0, ${i * ROW_HEIGHT})`);

      const labelBg = labelRows.append('rect')
        .attr('x', -margin.left).attr('y', 0)
        .attr('width', margin.left).attr('height', ROW_HEIGHT)
        .attr('class', styles.labelBg)
        .style('cursor', 'default');

      const iconPad = 26;
      const labelG = labelRows.append('g').attr('transform', `translate(-${margin.left - iconPad}, ${ROW_HEIGHT / 2})`);

      labelG.append('circle')
        .attr('r', 6).attr('cy', 0)
        .attr('fill', d => statusColors[d.status] || '#ccc')
        .attr('stroke', d => statusColors[d.status] || '#ccc')
        .attr('stroke-opacity', 0.4).attr('stroke-width', 4);

      const deviceNameFull = d => (d.name === 'server' ? 'FL Server' : d.name);
      const nameText = labelG.append('text')
        .text(deviceNameFull)
        .attr('x', iconPad)
        .attr('y', 0)
        .attr('dy', (labelSingleLine && !inModal) ? '0.15em' : '-0.3em')
        .attr('dominant-baseline', (labelSingleLine && !inModal) ? 'middle' : null)
        .attr('text-anchor', 'start')
        .attr('class', styles.labelDeviceName);

      const nameMaxWidth = margin.left - iconPad - 62;
      nameText.each(function(d){
        const sel = d3.select(this);
        fitWithEllipsis(sel, deviceNameFull(d), nameMaxWidth);
        d._displayName = sel.text();
        d._fullName = deviceNameFull(d);
        sel.append('title').text(deviceNameFull(d));
      });

      if (!labelSingleLine) {
        labelG.append('text')
          .text(d => d.summary)
          .attr('x', iconPad).attr('dy', '1.1em')
          .attr('text-anchor', 'start')
          .attr('class', styles.labelTaskInfo);
      }
      if (labelSingleLine && inModal) {
        const fmtSE = d3.timeFormat('%H:%M:%S');
        labelG.append('text')
          .text(d => {
            const t0 = d?.tasks?.[0]?.start ? fmtSE(new Date(d.tasks[0].start)) : '';
            const t1 = d?.tasks?.[0]?.end   ? fmtSE(new Date(d.tasks[0].end))   : '';
            return (t0 || t1) ? `${t0}  ~  ${t1}` : '';
          })
          .attr('x', iconPad)
          .attr('dy', '1.1em')
          .attr('text-anchor', 'start')
          .attr('class', styles.labelTaskInfo);
      }      

      function showDeviceTooltip(event, d) {
        const devAvgs = computeJobAverages(d.tasks || []);
        const globalAvgs = computeGlobalJobAverages(safeDevices);
        const globalMap = indexByJob(globalAvgs);

        const isServer = String(d?.name).toLowerCase() === 'server';

        const merged = devAvgs.map(({job, avgMs, count}) => {
          const g = globalMap.get(job);
          return {
            job,
            avgMs,
            count,
            globalAvgMs: g?.avgMs ?? 0,
            globalCount: g?.count ?? 0
          };
        });

        const rowH = 18;
        const baseH = 70;
        const h = baseH + Math.max(1, merged.length) * rowH;
        const { x, y } = getFixedTooltipPos(event, isServer ? 320 : 440, h, 12);

        setTooltip({
          visible: true,
          x, y,
          kind: 'device',
          task: '', duration: '', name: '', start: '', end: '',
          deviceDisplayName: d._fullName || deviceNameFull(d),
          averages: merged,
          hideGlobalForServer: isServer
        });
      }
      function moveDeviceTooltip(event) {
        setTooltip(prev => prev.visible ? { ...prev, ...getFixedTooltipPos(event, prev.hideGlobalForServer ? 320 : 440, 140, 12) } : prev);
      }
      function hideTooltip() {
        setTooltip(prev => ({ ...prev, visible: false }));
      }

      function showGlobalTooltip(event) {
        const avgs = computeGlobalJobAverages(safeDevices);
        const h = 64 + Math.max(1, avgs.length) * 18;
        const { x, y } = getFixedTooltipPos(event, 340, h, 12);
        setTooltip({
          visible: true,
          x, y,
          kind: 'global',
          task: '', duration: '', name: '', start: '', end: '',
          deviceDisplayName: 'FL Server 요약',
          averages: avgs,
          hideGlobalForServer: true
        });
      }

      if (!inModal) {
        labelBg.on('mouseenter', function (event, d) { showDeviceTooltip(event, d); })
          .on('mousemove', function (event) { moveDeviceTooltip(event); })
          .on('mouseleave', hideTooltip)
          .on('click', function (event, d) {
            if (String(d?.name).toLowerCase() === 'server') {
              event.stopPropagation();
              showGlobalTooltip(event);
            }
          });
      }

      if (!inModal) {
        nameText.on('mouseenter', function (event, d) { showDeviceTooltip(event, d); })
          .on('mousemove', function (event) { moveDeviceTooltip(event); })
          .on('mouseleave', hideTooltip)
          .on('click', function (event, d) {
            if (String(d?.name).toLowerCase() === 'server') {
              event.stopPropagation();
              showGlobalTooltip(event);
            }
          });
      }

      content.append('line').attr('x1', -margin.left).attr('x2', width).attr('y1', height).attr('y2', height).attr('class', styles.rowBottomDivider);
    }

    prevZoomDomainRef.current = zoomDomain;
    prevDataKeyRef.current = dataKey;
    shouldAnimNextZoomRef.current = false;
  }, [safeDevices, zoomDomain, containerWidth, innerWidth, dataKey, margin.left, margin.right, inModal, onRowClick]);

  useEffect(() => {
    if (!svgRef.current || !axisRef.current) return;

    const svg = d3.select(svgRef.current);
    let guide = svg.select('.guide-layer');
    if (guide.empty()) guide = svg.append('g').attr('class', 'guide-layer');
    guide.selectAll('*').remove();

    const height = safeDevices.length * ROW_HEIGHT;

    if (mouseX != null) {
      const g = guide.attr('transform', `translate(${margin.left}, 0)`);
      const x = clamp(mouseX, 0, innerWidth);
      g.append('line')
        .attr('class', styles.guideline)
        .attr('x1', x).attr('x2', x)
        .attr('y1', 0).attr('y2', height)
        .style('pointer-events', 'none');
    }

    let brushLayer = svg.select('.brush-layer');
    if (brushLayer.empty()) brushLayer = svg.append('g').attr('class', 'brush-layer');
    brushLayer.selectAll('*').remove();

    if (isBrushing && brushStartX != null && brushEndX != null) {
      const x0 = Math.min(brushStartX, brushEndX);
      const x1 = Math.max(brushStartX, brushEndX);
      const g = brushLayer.attr('transform', `translate(${margin.left}, 0)`);
      g.append('rect')
        .attr('x', x0)
        .attr('y', 0)
        .attr('width', Math.max(1, x1 - x0))
        .attr('height', height)
        .attr('fill', 'var(--color-primary)')
        .attr('opacity', 0.15)
        .attr('stroke', 'var(--color-primary)')
        .attr('stroke-width', 1)
        .style('pointer-events', 'none');
    }

    const axisSvg = d3.select(axisRef.current);
    let cursorLayer = axisSvg.select('.cursor-layer');
    if (cursorLayer.empty()) cursorLayer = axisSvg.append('g').attr('class', 'cursor-layer');
    cursorLayer.selectAll('*').remove();

    if (mouseX != null && innerWidth > 0) {
      const xScale = d3.scaleTime().domain(zoomDomain).range([0, innerWidth]);
      const clampedX = clamp(mouseX, 0, innerWidth);
      const xHeader = margin.left + clampedX;
      const t = xScale.invert(clampedX);
      const fmt = formatCursorTime(+zoomDomain[1] - +zoomDomain[0]);
      const labelText = fmt(t);

      const fontSize = 12; const padX = 12, padY = 4;
      const g = cursorLayer.append('g')
        .attr('class', 'cursor-time')
        .attr('transform', `translate(${xHeader + 74}, ${PILL_Y})`)
        .style('pointer-events', 'none');

      const text = g.append('text')
        .attr('dy', '0.35em')
        .attr('text-anchor', 'middle')
        .attr('class', styles.whiteText)
        .attr('font-size', fontSize)
        .text(labelText);
      const bbox = text.node().getBBox();
      g.insert('path', 'text')
        .attr('d', pillPath(bbox.width + padX * 2, bbox.height + padY * 2, 4, 12))
        .attr('fill', 'var(--color-primary)');
    }
  }, [mouseX, isBrushing, brushStartX, brushEndX, safeDevices.length, margin.left, innerWidth, zoomDomain]);

  const getLocalX = useCallback((event) => {
    if (!svgRef.current) return null;
    const bounds = svgRef.current.getBoundingClientRect?.();
    if (!bounds) return null;
    return clamp(event.clientX - bounds.left - margin.left, 0, innerWidth);
  }, [innerWidth, margin.left]);

  const finalizeBrush = useCallback(() => {
    if (!isBrushing || brushStartX == null || brushEndX == null) {
      setIsBrushing(false); setBrushStartX(null); setBrushEndX(null); return;
    }
    const px0 = Math.min(brushStartX, brushEndX);
    const px1 = Math.max(brushStartX, brushEndX);
    const MIN_PIXELS = 4;
    if (px1 - px0 < MIN_PIXELS) {
      setIsBrushing(false); setBrushStartX(null); setBrushEndX(null); return;
    }
    const xScale = d3.scaleTime().domain(zoomDomain).range([0, innerWidth]);
    let newMin = xScale.invert(px0);
    let newMax = xScale.invert(px1);
    const [bd0, bd1] = baseDomainRef.current;

    if (newMin < bd0) newMin = new Date(bd0);
    if (newMax > bd1) newMax = new Date(bd1);

    shouldAnimNextZoomRef.current = true;
    setZoomDomain([newMin, newMax]);

    setIsBrushing(false);
    setBrushStartX(null);
    setBrushEndX(null);
  }, [isBrushing, brushStartX, brushEndX, zoomDomain, innerWidth]);

  const handleMouseMove = useCallback((event) => {
    const x = getLocalX(event);
    if (x == null) return;
    setMouseX(x);
    if (isBrushing && brushStartX != null) setBrushEndX(x);
  }, [getLocalX, isBrushing, brushStartX]);

  const handleMouseDown = useCallback((event) => {
    const x = getLocalX(event);
    if (x == null) return;

    const now = Date.now();
    const { t: prevT, x: prevX } = lastClickRef.current;
    if (now - prevT < 280 && Math.abs(x - prevX) < 3) {
      shouldAnimNextZoomRef.current = true;
      setZoomDomain(baseDomainRef.current);
      lastClickRef.current = { t: 0, x: 0 };
      return;
    }
    lastClickRef.current = { t: now, x };

    setIsBrushing(true);
    setBrushStartX(x);
    setBrushEndX(x);
  }, [getLocalX]);

  const handleMouseUp = useCallback(() => { finalizeBrush(); }, [finalizeBrush]);
  const handleMouseLeave = useCallback(() => { finalizeBrush(); setMouseX(null); }, [finalizeBrush]);

  const handleWheel = useCallback((event) => {
    const [min, max] = zoomDomain;
    if (!innerWidth) return;

    if (event.altKey) {
      event.preventDefault();
      if (!svgRef.current) return;
      const bounds = svgRef.current.getBoundingClientRect?.();
      if (!bounds) return;
      const localX = clamp(event.clientX - bounds.left - margin.left, 0, innerWidth);
      const xScale = d3.scaleTime().domain(zoomDomain).range([0, innerWidth]);
      const centerTime = xScale.invert(localX);
      const span = +max - +min;
      const zoomFactor = event.deltaY > 0 ? 1.1 : 0.9;
      const [bd0, bd1] = baseDomainRef.current;
      const fullSpan = +bd1 - +bd0;
      const minSpan = ms.SECOND;
      const newSpan = clamp(span * zoomFactor, minSpan, fullSpan);
      const centerMs = +centerTime;
      let newMin = new Date(centerMs - (localX / innerWidth) * newSpan);
      let newMax = new Date(+newMin + newSpan);
      if (newMin < bd0) { newMin = new Date(bd0); newMax = new Date(+newMin + newSpan); }
      if (newMax > bd1) { newMax = new Date(bd1); newMin = new Date(+newMax - newSpan); }
      setZoomDomain([newMin, newMax]);
      return;
    }

    if (event.shiftKey) {
      event.preventDefault();
      const deltaPx = event.deltaY;
      const msPerPx = (+max - +min) / innerWidth;
      const offsetMs = deltaPx * msPerPx * 0.5;
      const [bd0, bd1] = baseDomainRef.current;
      let newMin = new Date(+min + offsetMs);
      let newMax = new Date(+max + offsetMs);
      if (newMin < bd0) { newMin = new Date(bd0); newMax = new Date(+newMin + (+max - +min)); }
      if (newMax > bd1) { newMax = new Date(bd1); newMin = new Date(+newMax - (+max - +min)); }
      setZoomDomain([newMin, newMax]);
      return;
    }
  }, [zoomDomain, innerWidth, margin.left]);

  useEffect(() => {
    if (!axisRef.current || !containerWidth) return;
    const axisSvg = d3.select(axisRef.current);
    let nowLayer = axisSvg.select('.now-layer');
    if (nowLayer.empty()) nowLayer = axisSvg.append('g').attr('class', 'now-layer');
    nowLayer.selectAll('*').remove();

    const xScale = d3.scaleTime().domain(zoomDomain).range([0, innerWidth]);
    const nowDate = new Date(nowTick);
    if (+nowDate < +zoomDomain[0] || +nowDate > +zoomDomain[1]) return;

    const nowXHeader = margin.left + xScale(nowDate);
    const labelText = 'Now';
    const fontSize = 12; const padX = 12, padY = 4;

    const g = nowLayer.append('g')
      .attr('transform', `translate(${nowXHeader + 22}, ${PILL_Y})`)
      .style('pointer-events', 'none');

    const text = g.append('text').attr('dy', '0.35em').attr('text-anchor', 'middle').attr('class', styles.whiteText).attr('font-size', fontSize).text(labelText);
    const bbox = text.node().getBBox();
    const wRect = bbox.width + padX * 2; const hRect = bbox.height + padY * 2;
    g.insert('path', 'text').attr('d', pillPath(wRect, hRect, 4, 12)).attr('fill', 'var(--color-border)');
  }, [nowTick, zoomDomain, containerWidth, innerWidth, margin.left]);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  if (isLoading) {
    const width = containerWidth || 960;
    const height = 10 * ROW_HEIGHT;
    const innerW = Math.max(1, width - margin.left - margin.right);
    return (
      <div ref={wrapperRef} className={styles.scrollHost} onDoubleClick={handleDoubleClick}>
        <div className={styles.headerPane}>
          <svg width="100%" height={HEADER_H}>
            <line x1={margin.left} x2={margin.left} y1={0} y2={HEADER_H} className={styles.headerColumnDivider} />
            <g transform={`translate(${margin.left}, ${HEADER_H - 1})`}>
              {Array.from({ length: 12 }).map((_, i) => {
                const x = (innerW / 12) * i;
                return (
                  <g key={i} transform={`translate(${x},0)`}>
                    <line y1={0} y2={TICK_SIZE + 3} className={styles.rowBottomDivider} />
                    <rect x={6} y={-HEADER_H + 14} width={48} height={10} className={`${styles.skelHeaderText}`} rx="6" ry="6" />
                  </g>
                );
              })}
            </g>
          </svg>
        </div>
        <div className={styles.chartPane}>
          <svg width="100%" height={height + margin.bottom}>
            <line x1={margin.left} x2={margin.left} y1={0} y2={height} className={styles.columnDivider} />
            <g transform={`translate(${margin.left}, 0)`}>
              {Array.from({ length: 10 }).map((_, i) => {
                const y = i * ROW_HEIGHT; const barH = Math.round(ROW_HEIGHT * 0.5);
                const barY = (ROW_HEIGHT - barH) / 2; const barW = Math.round(((72 + (i % 8) * 6) / 100) * innerW);
                const barX = 12;
                return (
                  <g key={i} transform={`translate(0, ${y})`}>
                    <line x1={-margin.left} x2={innerW} y1={0} y2={0} className={styles.rowTopDivider} />
                    <rect x={-margin.left} y={0} width={margin.left} height={ROW_HEIGHT} className={styles.labelBg} />
                    <rect x={barX} y={barY} width={barW} height={barH} rx="14" ry="14" className={`${styles.task} ${styles.skelBar}`} />
                  </g>
                );
              })}
              <line x1={-margin.left} x2={innerW} y1={height} y2={height} className={styles.rowBottomDivider} />
            </g>
          </svg>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        ref={wrapperRef}
        className={`${styles.scrollHost} ${className!=='' &&styles.modalChart} ${isBrushing ? styles.isBrushing : ''}`}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onDoubleClick={handleDoubleClick}
      >
        <div className={styles.headerPane}>
          <svg ref={axisRef} />
        </div>
        <div className={styles.chartPane}>
          <svg ref={svgRef} onDoubleClick={handleDoubleClick} />
        </div>
      </div>

      {tooltip.visible && tooltip.kind === 'task' && (
        <div
          className={`${styles.tooltip} ${styles.tooltipTask}`}
          style={{ left: tooltip.x, top: tooltip.y }}
        >
          <div className={styles.tooltipTitle}>
            {tooltip.task}
            {tooltip.duration ? <span className={`${styles.muted} ${styles.fw400}`}>· {tooltip.duration}</span> : null}
          </div>
          <div className={styles.mt2}>{tooltip.name}</div>
          <div className={styles.mt4}><span className={styles.muted}>Start</span>: {tooltip.start}</div>
          {tooltip.end ? (
            <div><span className={styles.muted}>End</span>: &nbsp;&nbsp;{tooltip.end}</div>
          ) : null}
        </div>
      )}

      {tooltip.visible && tooltip.kind === 'device' && (
        <div
          className={`${styles.tooltip} ${styles.tooltipDevice}`}
          style={{ left: tooltip.x, top: tooltip.y }}
        >
          <div className={styles.tooltipTitle}>{tooltip.deviceDisplayName}</div>
          {tooltip.averages.length === 0 ? (
            <div className={styles.muted}>평균 소요시간을 계산할 작업이 없어요</div>
          ) : (
            <div>
              {!tooltip.hideGlobalForServer ? (
                <div className={styles.avgGrid}>
                  <div className={styles.avgHead}>Job</div>
                  <div className={`${styles.avgHead} ${styles.textRight}`}>Compare</div>
                  <div className={`${styles.avgHead} ${styles.textRight}`}>Device</div>
                  <div className={`${styles.avgHead} ${styles.textRight}`}>Total</div>
                  {tooltip.averages.map(({ job, avgMs, count, globalAvgMs, globalCount }) => {
                    const { label, color } = diffInfo(avgMs, globalAvgMs);
                    return (
                      <React.Fragment key={job}>
                        <div>{job}</div>
                        <div className={`${styles.textRight} ${styles.nowrap}`}>
                          {label ? <span className={styles.fw700} style={{ color }}>{label}</span> : null}
                        </div>
                        <div className={`${styles.textRight} ${styles.nowrap} ${styles.numTabular}`}>
                          <b>{formatDurationMs(avgMs)}</b>
                        </div>
                        <div className={`${styles.textRight} ${styles.nowrap} ${styles.numTabular}`}>
                          <b>{formatDurationMs(globalAvgMs)}</b>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              ) : (
                <div className={styles.avgGridSimple}>
                  <div className={styles.avgHead}>Job</div>
                  <div className={`${styles.avgHead} ${styles.textRight}`}>Avg</div>
                  {tooltip.averages.map(({ job, avgMs, count }) => (
                    <React.Fragment key={job}>
                      <div>{job}</div>
                      <div className={styles.textRight}>
                        <b>{formatDurationMs(avgMs)}</b> <span className={styles.muted}>({count}회)</span>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {tooltip.visible && tooltip.kind === 'global' && (
        <div
          className={`${styles.tooltip} ${styles.tooltipGlobal}`}
          style={{ left: tooltip.x, top: tooltip.y }}
        >
          <div className={styles.tooltipTitle}>{tooltip.deviceDisplayName}</div>
          {tooltip.averages.length === 0 ? (
            <div className={styles.muted}>평균 소요시간을 계산할 작업이 없어요</div>
          ) : (
            <div>
              <ul className={styles.listReset}>
                {tooltip.averages.map(({job, avgMs, count}) => (
                  <li key={job} className={styles.line14}>
                    {job}: <b>{formatDurationMs(avgMs)}</b> <span className={styles.muted}>({count}회)</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </>
  );
});